import { z } from "zod";
import {
  TopolBlockCssClassSchema,
  TopolBlockAlignSchema,
  TopolVertcalAlignSchema,
  TopolBlockSchema,
  TopolColorSchema,
  TopolColorBearingSchema,
} from "../commonSchema";

export const TopolProductBlockSchema = TopolBlockSchema.extend({
  tagName: z.literal("mj-product"),
  blockName: z.literal("product").catch("product"),
  attributes: z
    .object({
      "columns-layout": z
        .enum(["one-column", "text-image", "image-text"])
        .optional()
        .catch(undefined),

      // Image
      image: z
        .string()
        .trim()
        .catch(
          "https://s3-eu-west-1.amazonaws.com/ecomail-assets/editor/pp1.png"
        ),
      "image-width": z.number().or(z.string()).catch(300),
      "image-alt": z.string().catch(""),
      "image-title": z.string().catch(""),
      "image-fluid-on-mobile": z
        .enum(["true", "false"])
        .optional()
        .catch(undefined),
      "image-align": TopolBlockAlignSchema.catch("center"),
      href: z.string().trim().catch(""),

      // Title
      title: z.string().catch(""),
      "raw-title": z.string().catch(""),
      "title-font-size": z.string().catch("13px"),
      "title-color": TopolColorSchema.catch("#000000"),
      "title-font-family": z.string().catch("Helvetica, Arial, sans-serif"),
      "title-font-weight": z.string().catch("normal"),
      "title-font-style": z.string().catch("normal"),
      "title-text-transform": z.string().catch("none"),
      "title-text-decoration": z.string().catch("none"),
      "title-align": TopolBlockAlignSchema.catch("center"),
      "title-padding": z.string().catch("0px 0px 0px 0px"),
      "title-line-height": z.number().optional().catch(undefined),
      "title-letter-spacing": z.string().optional().catch(undefined),

      // Description
      description: z.string().catch(""),
      "raw-description": z.string().catch(""),
      "description-font-size": z.string().catch("13px"),
      "description-color": TopolColorSchema.catch("#000000"),
      "description-font-family": z
        .string()
        .catch("Helvetica, Arial, sans-serif"),
      "description-font-weight": z.string().catch("normal"),
      "description-font-style": z.string().catch("normal"),
      "description-text-transform": z.string().catch("none"),
      "description-text-decoration": z.string().catch("none"),
      "description-align": TopolBlockAlignSchema.catch("center"),
      "description-padding": z.string().catch("0px 0px 0px 0px"),
      "description-line-height": z.number().optional().catch(undefined),
      "description-letter-spacing": z.string().optional().catch(undefined),

      // Price Before
      "price-before": z.string().catch(""),
      "raw-price-before": z.string().catch(""),
      "price-before-font-size": z.string().catch("13px"),
      "price-before-color": TopolColorSchema.catch("#000000"),
      "price-before-font-family": z
        .string()
        .catch("Helvetica, Arial, sans-serif"),
      "price-before-font-weight": z.string().catch("normal"),
      "price-before-font-style": z.string().catch("normal"),
      "price-before-text-transform": z.string().catch("none"),
      "price-before-align": z.string().catch("center"),
      "price-before-padding": z.string().catch("0px 0px 0px 0px"),
      "price-before-text-decoration": z.string().optional().catch("none"),
      "price-before-line-height": z.number().optional().catch(undefined),
      "price-before-letter-spacing": z.string().optional().catch(undefined),

      // Price
      price: z.string().catch(""),
      "raw-price": z.string().catch(""),
      "price-font-size": z.string().catch("13px"),
      "price-color": TopolColorSchema.catch("#000000"),
      "price-font-family": z.string().catch("Helvetica, Arial, sans-serif"),
      "price-font-weight": z.string().catch("normal"),
      "price-font-style": z.string().catch("normal"),
      "price-text-transform": z.string().catch("none"),
      "price-text-decoration": z.string().catch("none"),
      "price-align": z.string().catch("center"),
      "price-padding": z.string().catch("0px 0px 0px 0px"),
      "price-suffix": z.string().catch(""),
      "price-prefix": z.string().catch(""),
      "price-line-height": z.number().optional().catch(undefined),
      "price-letter-spacing": z.string().optional().catch(undefined),

      // Layout
      layout: z.string().catch(""),
      dynamic: z.boolean().optional().catch(undefined),

      // Button
      "button-align": TopolBlockAlignSchema.catch("center"),
      "button-text-align": TopolBlockAlignSchema.optional().catch("center"),
      "button-border-radius": z.string().catch("3px"),
      "button-padding": z.string().catch("20px 20px 20px 20px"),
      "button-background-color": TopolColorSchema.catch("#F5A623"),
      "button-gradient": TopolColorBearingSchema.optional().catch(undefined),
      "button-color": TopolColorSchema.catch("#ffffff"),
      "button-font-size": z.string().catch("13px"),
      "button-font-family": z.string().catch("Helvetica, Arial, sans-serif"),
      "button-font-weight": z.string().catch("normal"),
      "button-font-style": z.string().catch("normal"),
      "button-text-transform": z.string().catch("none"),
      "button-text-decoration": z.string().catch("none"),
      "button-inner-padding": z.string().catch("10px 20px 10px 20px"),
      "button-width": z.enum(["auto", "100%"]).optional().catch("auto"),
      button: z.string().catch(""),
      containerWidth: z.number().catch(600),

      "image-border-radius": z.string().optional().catch(undefined),
      "image-padding": z.string().optional().catch(undefined),
      product_id: z.number().or(z.string()).optional().catch(undefined),
      product_feed_id: z.number().or(z.string()).optional().catch(undefined),
      "vertical-align": TopolVertcalAlignSchema.optional().catch(undefined),
      "css-class": TopolBlockCssClassSchema.optional().catch(undefined),
    })
    .default({} as any),
  "button-hover": z
    .object({
      "background-color": TopolColorSchema.optional().catch(undefined),
      color: TopolColorSchema.optional().catch(undefined),
      gradient: TopolColorBearingSchema.optional(),
    })
    .optional()
    .catch(undefined),
});

export type TopolProductBlock = z.infer<typeof TopolProductBlockSchema>;
